import React, { useState, useEffect } from 'react';
import FeatherIcon from 'feather-icons-react';
import FormHandler from '../../shared/utils/FormHandler';
import { validatePrivilegeForm } from '../../utils/validation';
import { createPrivilege, updatePrivilege } from '../../utils/mainServices';
import { toast } from 'react-toastify';

const PrivilegePopup = ({
                            isOpen,
                            onClose,
                            onSave,
                            type = 'add-high',
                            defaultValues = null,
                            existingPrivileges = [],
                            parentId = null
                        }) => {
    const [newActionText, setNewActionText] = useState('');
    const [formErr, setFormErr] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { values, errors, handleSubmit, handleChange, handleOnBlur, initForm, setValue, setError, refresh } =
        FormHandler(submitPrivilegeForm, (vals) => validatePrivilegeForm(vals, existingPrivileges, type, defaultValues?.id, parentId));

    async function submitPrivilegeForm() {
        setIsSubmitting(true);

        try {
        const payload = {
                name: values.name?.trim(),
                description: values.description?.trim() || '',
                actions: values.actions || [],
                child: [],
                childIds: []
            };

            if (type === 'add-high') {
                payload.parentId = 'high-privilege';
            } else if (type === 'add-new') {
                payload.parentId = parentId;
            } else if (type === 'update') {
                payload.parentId = defaultValues?.parentId || 'high-privilege';
            }

            let result;
            if (type === 'update') {
                payload.id = defaultValues.id;
                result = await updatePrivilege(defaultValues.id, payload);
            } else {

                result = await createPrivilege(payload);
            }

            if (result.message === "Success") {
                const successMsg = type === 'update' ? 'Privilege Updated Successfully' : 'Privilege Created Successfully';
                toast.success(successMsg);
                await onSave(values);
                handleClose();
            } else {
                setFormErr(result?.message || 'Failed to save privilege');
                toast.error(result?.message || 'Failed to save privilege');
            }
        } catch (error) {
            console.error('Error saving privilege:', error);
            if (error?.response && error.response.status === 422) {
                setFormErr(error.response?.data?.message || 'Validation failed');
                toast.error(error.response?.data?.message || 'Validation failed');
            } else {
                setFormErr(error?.response?.data?.message || error?.message || 'Failed to save privilege');
                toast.error(error?.response?.data?.message || error?.message || 'Failed to save privilege');
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleClose = () => {
        initForm({ name: '', description: '', actions: [] });
        setNewActionText('');
        setFormErr('');
        onClose();
    };

    const addAction = () => {
        if (!newActionText.trim()) {
            setError('newActionInput', 'Action name is required');
            return;
        }
        if (values.actions && values.actions.some(action => action.action_name.toLowerCase() === newActionText.toLowerCase())) {
            return;
        }
        const newActions = [...(values.actions || []), { action_name: newActionText.trim(), selected: true }];
        setValue({ actions: newActions });
        setNewActionText('');
        setError('newActionInput', null);
        if (newActions.length > 0) {
            setError('actions', null);
        }
    };

    const removeAction = (actionToRemove) => {
        setValue({ actions: values.actions.filter(action => action.action_name !== actionToRemove.action_name) });
    };

    useEffect(() => {
        if (isOpen) {
            initForm({
                name: defaultValues?.name || '',
                description: defaultValues?.description || '',
                actions: defaultValues?.actions || []
            });
            setNewActionText('');
            setFormErr('');
        }
    }, [isOpen, defaultValues]);

    if (!isOpen) return null;

    const getPopupTitle = () => {
        switch (type) {
            case 'add-high':
                return 'Add New High Privilege';
            case 'add-new':
                return 'Add New Sub-Privilege';
            case 'update':
                return 'Update Privilege';
            default:
                return 'Add New Privilege';
        }
    };

    const getButtonText = () => {
        switch (type) {
            case 'update':
                return 'Update';
            default:
                return 'Add';
        }
    };

    return (
        <div className="sa-popup-bg">
            <div className="sa-popup">
                <form onSubmit={handleSubmit} className="sa-modal-box-style" noValidate>
                    <div className="sa-popup-header">
                        <span className="sa-model-heading">
                            {getPopupTitle()}
                        </span>
                        <div className="sa-popup-close-icon" onClick={handleClose}>
                            <FeatherIcon className="sa-modal-close-icon" icon="x" />
                        </div>
                    </div>

                    <div className="sa-popup-content">
                        <div className="col-md-6">
                            <div className="form-group m-b-16">
                                <label htmlFor="name">Privilege Name</label>
                                <input
                                    type="text"
                                    className={`form-control ${errors.name ? "warning-input" : ""}`}
                                    onBlur={handleOnBlur}
                                    onChange={handleChange}
                                    value={values.name || ''}
                                    name="name"
                                    id="name"
                                    placeholder={type === 'add-high' ? "Enter high privilege name " : "Enter sub-privilege name "}
                                />
                                {errors.name && <p className="warning-input-msg">{errors.name}</p>}
                            </div>
                        </div>

                        <div className="col-md-6">
                            <div className="form-group m-b-16">
                                <label htmlFor="description">Description</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    onBlur={handleOnBlur}
                                    onChange={handleChange}
                                    value={values.description || ''}
                                    name="description"
                                    id="description"
                                    placeholder="Enter description"
                                />
                            </div>
                        </div>

                        <div className="col-md-12">
                            <div className="form-group m-b-16">
                                <label htmlFor="actions">Actions</label>
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                    <input
                                        type="text"
                                        className={`form-control ${errors.newActionInput ? "warning-input" : ""}`}
                                        value={newActionText}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setNewActionText(val);
                                            if (val.trim() && errors.newActionInput) {
                                                setError('newActionInput', null);
                                            }
                                        }}
                                        placeholder="Enter action name"
                                        name="newActionInput"
                                        id="newActionInput"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addAction();
                                            }
                                        }}
                                    />
                                    <button
                                        type="button"
                                        onClick={addAction}
                                        className="sa-popup-secondary-btn-style"
                                        style={{ backgroundColor: '#5895E9', color: 'white', minWidth: '80px' }}
                                    >
                                        Add
                                    </button>
                                </div>
                                {errors.newActionInput && <p className="warning-input-msg">{errors.newActionInput}</p>}

                                <div style={{
                                    border: `1px solid ${errors.actions ? '#dc3545' : '#ddd'}`,
                                    borderRadius: '4px',
                                    padding: '12px',
                                    minHeight: '100px',
                                    maxHeight: '150px',
                                    overflowY: 'auto',
                                    backgroundColor: '#f9f9f9'
                                }}>
                                    {values.actions && values.actions.length > 0 ? (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {values.actions.map((action, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        backgroundColor: '#5895E9',
                                                        color: 'white',
                                                        padding: '4px 12px',
                                                        borderRadius: '4px'
                                                    }}
                                                >
                                                    <span>{action.action_name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAction(action)}
                                                        style={{ marginLeft: '8px', background: 'none', border: 'none', color: 'white' }}
                                                    >
                                                        <FeatherIcon icon="x" size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p style={{ color: '#666', textAlign: 'center' }}>No actions added yet</p>
                                    )}
                                </div>
                                {errors.actions && <p className="warning-input-msg">{errors.actions}</p>}
                            </div>
                        </div>
                    </div>

                    {formErr && <div className="login-warning-msg">{formErr}</div>}

                    <div className="sa-popup-btn">
                        <button
                            className="sa-popup-secondary-btn-style sa-popup-cancel-btn-style"
                            type="button"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="sa-popup-secondary-btn-style"
                            disabled={isSubmitting}
                            style={{ backgroundColor: '#5895E9', color: 'white' }}
                        >
                            {isSubmitting ? 'Saving...' : getButtonText()}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PrivilegePopup;
